// ─── LineChartDataModals ──────────────────────────────────────────────────────
// Two popup overlays surfaced from the Line Chart widget's three-dot menu:
//
//   DataTableModal   — shows the underlying time-series rows in a tabular
//                      view, with a multi-line column header that nests
//                      Site → Device → Sensor → Parameter (Unit) per the
//                      latest XD/PSD update from the client.
//
//   StatisticsModal  — shows per-series summary statistics (count, min,
//                      max, average, median, std-dev).
//
// Each modal carries its own `⋮` overflow menu offering:
//   Download PDF / Download PNG Image / Download CSV / Download XLS / Print
//
// Download/print behaviour is delegated through props so the surrounding
// widget keeps a single source of truth for data extraction and chart-
// rendering helpers (CSV/XLS/PNG already live in DashboardWidgets.jsx).
// ──────────────────────────────────────────────────────────────────────────────

import { useEffect, useRef, useState } from 'react'
import { X } from 'lucide-react'

// Default Recharts palette used by LineChartWidget — duplicated locally so
// the statistics modal can show the matching dot per series without
// requiring the parent to thread the full COLORS array through.
const FALLBACK_COLORS = ['#358ECD', '#56B565', '#E36B6B', '#F1948A', '#8B5CF6', '#06B6D4']

// ── Shared `⋮` overflow menu ────────────────────────────────────────────────
function ActionMenu({ items, label = 'Actions' }) {
    const [open, setOpen] = useState(false)
    const ref = useRef(null)
    useEffect(() => {
        if (!open) return
        const handler = (e) => { if (!ref.current?.contains(e.target)) setOpen(false) }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [open])
    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="p-1 text-gray-400 hover:text-gray-600 rounded transition"
                title={label}
                aria-label={label}
            >
                <svg width="16" height="16" viewBox="0 0 4 16" fill="currentColor">
                    <circle cx="2" cy="2"  r="1.5" />
                    <circle cx="2" cy="8"  r="1.5" />
                    <circle cx="2" cy="14" r="1.5" />
                </svg>
            </button>
            {open && (
                <div className="absolute right-0 top-full mt-1 w-52 bg-white border border-gray-200 rounded-xl shadow-2xl z-[10001] py-1.5 text-[13px] text-gray-700">
                    {items.map((it, i) =>
                        it === '-' ? (
                            <div key={i} className="my-1.5 border-t border-gray-100" />
                        ) : (
                            <button
                                key={it.label}
                                type="button"
                                onClick={() => { setOpen(false); it.onClick?.() }}
                                className="w-full text-left px-4 py-2 hover:bg-gray-50 transition"
                            >
                                {it.label}
                            </button>
                        )
                    )}
                </div>
            )}
        </div>
    )
}

// ── Modal shell — centred overlay with backdrop ─────────────────────────────
function ModalShell({ open, onClose, title, menuItems, children }) {
    // Close on Escape
    useEffect(() => {
        if (!open) return
        const handler = (e) => { if (e.key === 'Escape') onClose?.() }
        document.addEventListener('keydown', handler)
        return () => document.removeEventListener('keydown', handler)
    }, [open, onClose])

    if (!open) return null
    return (
        <div
            className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 p-6"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-lg shadow-2xl w-full max-w-5xl max-h-[85vh] flex flex-col overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
                    <h3 className="text-base font-semibold text-gray-800">{title}</h3>
                    <div className="flex items-center gap-2">
                        <ActionMenu items={menuItems} />
                        <button
                            type="button"
                            onClick={onClose}
                            className="p-1 text-gray-400 hover:text-gray-700 rounded transition"
                            aria-label="Close"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>
                <div className="flex-1 overflow-auto">{children}</div>
            </div>
        </div>
    )
}

// ── Data Table modal ────────────────────────────────────────────────────────
//
// `columns` lets the parent describe each parameter's header lines (Site,
// Device, Sensor, Parameter+Unit). Each entry:
//   { paramId, headerLines: ['Site 2', 'X3-4G-01078', ...], accessor: row => row[`p_${id}`] }
//
export function DataTableModal({
    open,
    onClose,
    title = 'Data Table',
    data = [],
    columns = [],
    formatTimestamp = String,
    decimals = 2,
    downloadPDF,
    downloadPNG,
    downloadCSV,
    downloadXLS,
    printDataTable,
}) {
    const menuItems = [
        { label: 'Download PDF',       onClick: downloadPDF },
        { label: 'Download PNG Image', onClick: downloadPNG },
        { label: 'Download CSV',       onClick: downloadCSV },
        { label: 'Download XLS',       onClick: downloadXLS },
        '-',
        { label: 'Print Data Table',   onClick: printDataTable },
    ]

    return (
        <ModalShell open={open} onClose={onClose} title={title} menuItems={menuItems}>
            <div className="px-5 py-4">
                <div className="overflow-auto border border-gray-200 rounded">
                    <table className="w-full text-xs border-collapse">
                        <thead className="bg-gray-50">
                            <tr>
                                <th
                                    rowSpan={1}
                                    className="sticky top-0 left-0 bg-gray-50 text-left px-4 py-3 font-semibold text-gray-600 border-b border-r border-gray-200 whitespace-nowrap align-top"
                                    style={{ minWidth: 220 }}
                                >
                                    Timestamp
                                </th>
                                {columns.map((col) => (
                                    <th
                                        key={col.paramId}
                                        className="sticky top-0 bg-gray-50 text-left px-4 py-3 font-normal text-gray-700 border-b border-r border-gray-200 align-top"
                                        style={{ minWidth: 180 }}
                                    >
                                        {col.headerLines.map((line, idx) => (
                                            <div
                                                key={idx}
                                                className={
                                                    idx === 0
                                                        ? 'font-semibold text-gray-800 leading-tight'
                                                        : 'text-gray-500 leading-tight'
                                                }
                                            >
                                                {line}
                                            </div>
                                        ))}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {data.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan={columns.length + 1}
                                        className="px-4 py-6 text-center text-gray-400"
                                    >
                                        No data available for this period.
                                    </td>
                                </tr>
                            ) : (
                                data.map((row, i) => (
                                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                                        <td className="px-4 py-2 text-gray-600 border-b border-r border-gray-100 whitespace-nowrap">
                                            {formatTimestamp(row.x)}
                                        </td>
                                        {columns.map((col) => {
                                            const v = col.accessor(row)
                                            return (
                                                <td
                                                    key={col.paramId}
                                                    className="px-4 py-2 text-gray-700 border-b border-r border-gray-100"
                                                >
                                                    {v == null || v === '' ? '—' : Number(v).toFixed(decimals)}
                                                </td>
                                            )
                                        })}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                <p className="text-[11px] text-gray-400 mt-2">{data.length.toLocaleString()} rows</p>
            </div>
        </ModalShell>
    )
}

// ── Statistics modal ───────────────────────────────────────────────────────
export function StatisticsModal({
    open,
    onClose,
    title = 'Statistics',
    statistics = [],
    seriesColor = (i) => FALLBACK_COLORS[i % FALLBACK_COLORS.length],
    downloadPDF,
    downloadPNG,
    downloadCSV,
    downloadXLS,
    printStatistics,
}) {
    const menuItems = [
        { label: 'Download PDF',       onClick: downloadPDF },
        { label: 'Download PNG Image', onClick: downloadPNG },
        { label: 'Download CSV',       onClick: downloadCSV },
        { label: 'Download XLS',       onClick: downloadXLS },
        '-',
        { label: 'Print Statistics',   onClick: printStatistics },
    ]
    const headers = ['Parameter', 'Unit', 'Count', 'Min', 'Max', 'Average', 'Median', 'Std Dev']

    return (
        <ModalShell open={open} onClose={onClose} title={title} menuItems={menuItems}>
            <div className="px-5 py-4">
                <div className="overflow-auto border border-gray-200 rounded">
                    <table className="w-full text-xs border-collapse">
                        <thead className="bg-gray-50">
                            <tr>
                                {headers.map((h) => (
                                    <th
                                        key={h}
                                        className="sticky top-0 bg-gray-50 text-left px-4 py-3 font-semibold text-gray-600 border-b border-r border-gray-200 whitespace-nowrap"
                                    >
                                        {h}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {statistics.length === 0 ? (
                                <tr>
                                    <td colSpan={headers.length} className="px-4 py-6 text-center text-gray-400">
                                        No statistics available.
                                    </td>
                                </tr>
                            ) : (
                                statistics.map((s, i) => (
                                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                                        <td className="px-4 py-2 border-b border-r border-gray-100 font-medium text-gray-700 whitespace-nowrap">
                                            <span
                                                className="inline-block w-2 h-2 rounded-full mr-1.5 align-middle"
                                                style={{ backgroundColor: seriesColor(s.idx ?? i) }}
                                            />
                                            {s.name}
                                        </td>
                                        <td className="px-4 py-2 border-b border-r border-gray-100 text-gray-500">{s.unit || '—'}</td>
                                        <td className="px-4 py-2 border-b border-r border-gray-100 text-gray-700">{s.count?.toLocaleString() ?? '—'}</td>
                                        <td className="px-4 py-2 border-b border-r border-gray-100 text-blue-600 font-medium">{s.min ?? '—'}</td>
                                        <td className="px-4 py-2 border-b border-r border-gray-100 text-red-500 font-medium">{s.max ?? '—'}</td>
                                        <td className="px-4 py-2 border-b border-r border-gray-100 text-gray-700">{s.avg ?? '—'}</td>
                                        <td className="px-4 py-2 border-b border-r border-gray-100 text-gray-700">{s.median ?? '—'}</td>
                                        <td className="px-4 py-2 border-b border-r border-gray-100 text-gray-700">{s.stddev ?? '—'}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </ModalShell>
    )
}
